/**
 * Holiday Service
 * API integration for Holiday CRUD operations
 *
 * @version 2025.1.0
 * @description Serviço de integração com API de Feriados
 */
import apiClient from './apiClient';
import {
  Holiday,
  HolidayFilters,
  HolidayFormData,
  HolidayListResponse,
  HolidayCheckResult,
  HolidayImportResult,
  HolidayType,
  HOLIDAY_TYPE_LABELS,
  UF_OPTIONS,
  getAvailableYears,
} from '../types/holiday';

// Re-export types for convenience
export * from '../types/holiday';

export interface HolidayListParams extends HolidayFilters {
  page?: number;
  limit?: number;
}

class HolidayService {
  private baseUrl = '/holidays';

  /**
   * Get paginated list of holidays
   */
  async getHolidays(params: HolidayListParams = {}): Promise<HolidayListResponse> {
    const { page = 1, limit = 50, ...filters } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (filters.year) queryParams.append('year', filters.year.toString());
    if (filters.type) queryParams.append('type', filters.type);
    if (filters.state) queryParams.append('state', filters.state);
    if (filters.city) queryParams.append('city', filters.city);
    if (filters.companyId) queryParams.append('companyId', filters.companyId.toString());
    if (filters.isActive !== undefined) queryParams.append('isActive', filters.isActive.toString());
    if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
    if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);

    // httpClient.get returns the response body directly (not axios response wrapper)
    // Response structure: { data: Holiday[], pagination: { total, page, limit, totalPages } }
    const response = await apiClient.get(`${this.baseUrl}?${queryParams.toString()}`) as {
      data: Holiday[];
      pagination?: { total: number; page: number; limit: number; totalPages: number };
    };

    return {
      holidays: response.data ?? [],
      total: response.pagination?.total ?? 0,
      page: response.pagination?.page || page,
      limit: response.pagination?.limit || limit,
      totalPages: response.pagination?.totalPages || Math.ceil((response.pagination?.total ?? 0) / limit),
    };
  }

  /**
   * Get holiday by ID
   */
  async getHolidayById(id: string | number): Promise<Holiday> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data.data || response.data;
  }

  /**
   * Get holidays by date
   */
  async getByDate(date: string, options?: { state?: string; city?: string; companyId?: number }): Promise<Holiday[]> {
    const queryParams = new URLSearchParams();
    if (options?.state) queryParams.append('state', options.state);
    if (options?.city) queryParams.append('city', options.city);
    if (options?.companyId) queryParams.append('companyId', options.companyId.toString());

    const queryString = queryParams.toString();
    const url = `${this.baseUrl}/date/${date}${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get(url);
    return response.data.data ?? [];
  }

  /**
   * Check if a date is a holiday
   */
  async checkHoliday(date: string, options?: { state?: string; city?: string; companyId?: number }): Promise<HolidayCheckResult> {
    const queryParams = new URLSearchParams({ date });
    if (options?.state) queryParams.append('state', options.state);
    if (options?.city) queryParams.append('city', options.city);
    if (options?.companyId) queryParams.append('companyId', options.companyId.toString());

    const response = await apiClient.get(`${this.baseUrl}/check?${queryParams.toString()}`);
    return response.data.data || response.data;
  }

  /**
   * Get upcoming holidays
   */
  async getUpcoming(months: number = 3): Promise<Holiday[]> {
    const response = await apiClient.get(`${this.baseUrl}/upcoming?months=${months}`);
    return response.data.data ?? [];
  }

  /**
   * Get holidays for a company (national + state + municipal + company)
   */
  async getForCompany(
    companyId: number,
    state: string,
    city: string,
    startDate: string,
    endDate: string
  ): Promise<Holiday[]> {
    const queryParams = new URLSearchParams({
      state,
      city,
      startDate,
      endDate,
    });

    const response = await apiClient.get(`${this.baseUrl}/company/${companyId}?${queryParams.toString()}`);
    return response.data.data ?? [];
  }

  /**
   * Create new holiday
   */
  async createHoliday(data: HolidayFormData): Promise<Holiday> {
    const response = await apiClient.post(this.baseUrl, data);
    return response.data.data || response.data;
  }

  /**
   * Update holiday by ID
   */
  async updateHoliday(id: string | number, data: Partial<HolidayFormData>): Promise<Holiday> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    return response.data.data || response.data;
  }

  /**
   * Delete holiday by ID
   */
  async deleteHoliday(id: string | number): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Import national holidays for a year
   */
  async importNationalHolidays(year: number): Promise<HolidayImportResult> {
    const response = await apiClient.post(`${this.baseUrl}/import/${year}`);
    return response.data.data || response.data;
  }

  /**
   * Get holiday types
   */
  async getTypes(): Promise<Array<{ key: string; value: HolidayType; label: string }>> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/types`);
      return response.data.data ?? this.getTypeOptions();
    } catch {
      return this.getTypeOptions();
    }
  }

  /**
   * Get holiday type options for select dropdowns
   */
  getTypeOptions(): Array<{ key: string; value: HolidayType; label: string }> {
    return Object.entries(HOLIDAY_TYPE_LABELS).map(([value, label]) => ({
      key: value,
      value: value as HolidayType,
      label,
    }));
  }

  /**
   * Get UF options for select dropdowns
   */
  getUfOptions(): Array<{ value: string; label: string }> {
    return UF_OPTIONS.map(uf => ({ value: uf, label: uf }));
  }

  /**
   * Get year options for select dropdowns
   */
  getYearOptions(): Array<{ value: number; label: string }> {
    return getAvailableYears().map(year => ({
      value: year,
      label: year.toString(),
    }));
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const response = await apiClient.get(`${this.baseUrl}/health`);
    return response.data.data || response.data;
  }
}

// Export singleton instance
export const holidayService = new HolidayService();
export default holidayService;
